import { CommonModule } from "@angular/common";
import { Component, forwardRef, Input, OnInit, ViewChild } from "@angular/core";
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormsModule,
} from "@angular/forms";
import { parse } from "css";
import { GoogleFontPickerComponent } from "./google-font-picker.component";
import { firstValueFrom } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "environments/environment";
import { OverlayPanelModule } from "primeng/overlaypanel";
import {
  ColorPickerComponent,
  ColorPickerDirective,
  ColorPickerModule,
} from "ngx-color-picker";

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    GoogleFontPickerComponent,
    ColorPickerModule,
    OverlayPanelModule,
  ],
  selector: "nis-brand-editor",
  templateUrl: "./brand-editor.component.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BrandEditorComponent),
      multi: true,
    },
  ],
})
export class BrandEditorComponent implements ControlValueAccessor, OnInit {
  css: string = "";
  colors = [
    {
      key: "--primary-color",
      name: $localize`Primary Color`,
    },
    {
      key: "--secondary-color",
      name: $localize`Secondary Color`,
    },
    {
      key: "--contrast-color",
      name: $localize`Tertiary Color`,
    },
  ];
  variables: { [key: string]: string } = {};
  currentColor: { key: string; value: string } = { key: "", value: "" };
  private onChange: (value: any) => void;
  private onTouched: () => void;
  disabled: boolean;
  fonts: any[] = [];

  constructor(private http: HttpClient) {}

  async ngOnInit() {
    const response = await firstValueFrom(
      await this.http.get(
        `https://www.googleapis.com/webfonts/v1/webfonts?key=${environment.google_font_api_key}`
      )
    );
    this.fonts = response["items"];
  }

  writeValue(value: string): void {
    if (value) {
      this.css = value;
      const parsedCss = parse(value);
      parsedCss.stylesheet.rules.forEach((rule: any) => {
        if (
          rule.selectors?.includes(".nis-catalog") ||
          rule.selectors?.includes("body")
        ) {
          rule.declarations.forEach((declaration: any) => {
            if (declaration.property.startsWith("--")) {
              this.variables[declaration.property] = declaration.value;
            }
          });
        }
      });
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange() {
    this.onChange(this.css);
    this.onTouched();
  }

  update(fontName: string) {
    const font = this.fonts.find(
      (font) => font.family === fontName.replace(/'/g, "").replace(/"/g, "")
    );
    let variants = "";
    if (font) {
      variants = font.variants.join(",");
    }
    return `@import url('https://fonts.googleapis.com/css?family=${encodeURIComponent(
      fontName.replace(/'/g, "").replace(/"/g, "") +
        (variants.length > 0 ? ":" + variants : "")
    )}');`;
  }

  generateStyleSelector(key: string, value: string) {
    return `\t${key}: ${value};\n`;
  }

  async updateStyle() {
    let branding = "";
    const primaryFontFamily = this.variables["--primary-font-family"];
    if (primaryFontFamily) {
      branding += this.update(primaryFontFamily) + "\n";
    }
    const secondaryFontFamily = this.variables["--secondary-font-family"];
    if (secondaryFontFamily) {
      branding += this.update(secondaryFontFamily) + "\n";
    }

    branding += "body {\n";
    branding += "\t/* Colors */\n";
    for (let color of this.colors) {
      branding += this.generateStyleSelector(
        color.key,
        this.variables[color.key]
      );
    }
    branding += "\n";
    branding += "\t/* Fonts */\n";
    if (primaryFontFamily) {
      branding += this.generateStyleSelector(
        "--primary-font-family",
        primaryFontFamily
      );
    }
    if (secondaryFontFamily) {
      branding += this.generateStyleSelector(
        "--secondary-font-family",
        secondaryFontFamily
      );
    }
    branding += "}";
    this.onChange(branding);
  }

  getFontColor(color: string) {
    let r, g, b;

    if (color.startsWith("#")) {
      // Convert hex to RGB
      const hex = color.replace("#", "");
      r = parseInt(hex.slice(0, 2), 16);
      g = parseInt(hex.slice(2, 4), 16);
      b = parseInt(hex.slice(4, 6), 16);
    } else if (color.startsWith("rgb")) {
      // Extract RGB values from rgb(xx, xx, xx) format
      const rgbValues = color.match(/\d+/g);
      if (rgbValues && rgbValues.length === 3) {
        [r, g, b] = rgbValues.map(Number);
      } else {
        // If the format is invalid, return a default color
        return "black";
      }
    } else {
      // If the color format is unrecognized, return a default color
      return "black";
    }

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? "black" : "white";
  }
}
